/**
 * Coordinate format utilities.
 *
 * GeoJSON/MongoDB/Mapbox use [longitude, latitude].
 * Some sources (e.g. GPS, mobile) send [latitude, longitude].
 *
 * @example
 * // Input: [12.97194, 77.59369] (lat, lng - Bangalore)
 * toGeoJSON([12.97194, 77.59369], { fromLatLng: true })
 * // Output: [77.59369, 12.97194] (lng, lat - GeoJSON)
 */

/**
 * Convert coordinates to GeoJSON format [longitude, latitude].
 * @param {number[]} coords - [lat, lng] or [lng, lat]
 * @param {Object} options
 * @param {boolean} options.fromLatLng - If true, input is [lat, lng]; swap to [lng, lat]
 * @returns {number[]} [longitude, latitude] for Mapbox/GeoJSON
 */
export const toGeoJSON = (coords, { fromLatLng = false } = {}) => {
  if (!coords || !Array.isArray(coords) || coords.length < 2) return coords;
  const [a, b] = coords;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return coords;
  return fromLatLng ? [b, a] : [a, b];
};

/**
 * Format coordinates for display (e.g. "12.9719° N, 77.5937° E").
 * @param {number[]} coords - [lat, lng] or [lng, lat]
 * @param {Object} options
 * @param {boolean} options.fromLatLng - If true, input is [lat, lng]
 * @returns {string} Formatted string or empty string if invalid
 */
export const formatCoordinates = (coords, { fromLatLng = false } = {}) => {
  if (!coords || coords.length < 2) return '';
  const [lon, lat] = toGeoJSON(coords, { fromLatLng });
  const latDir = lat >= 0 ? 'N' : 'S';
  const lonDir = lon >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(4)}° ${latDir}, ${Math.abs(lon).toFixed(4)}° ${lonDir}`;
};

const DMS_COMPONENT_RE = /^(-?\d{1,3}(?:\.\d+)?)\s*(?:°|º|deg)?\s*(?:(\d{1,2}(?:\.\d+)?)\s*(?:'|′|min)?\s*)?(?:(\d{1,2}(?:\.\d+)?)\s*(?:"|″|sec)?\s*)?([NSEW])?$/i;

/**
 * Parse a single latitude or longitude in decimal or DMS form.
 * @param {string} str - e.g. "32.7083", "32°42'29.8\"N", "-117.1803", "117°10'49.0\"W"
 * @returns {number|null}
 */
export const parseCoordinateComponent = (str) => {
  if (!str || typeof str !== 'string') return null;
  const cleaned = str.trim();
  if (!cleaned) return null;

  const m = cleaned.match(DMS_COMPONENT_RE);
  if (!m) return null;

  const rawDeg = parseFloat(m[1]);
  if (!Number.isFinite(rawDeg)) return null;

  const minutes = parseFloat(m[2]) || 0;
  const seconds = parseFloat(m[3]) || 0;
  const direction = (m[4] || '').toUpperCase();
  const hasDmsParts = m[2] != null || m[3] != null || /[°'″′"]/.test(cleaned);

  let value;
  if (hasDmsParts) {
    value = Math.abs(rawDeg) + minutes / 60 + seconds / 3600;
  } else {
    value = rawDeg;
  }

  if (direction === 'S' || direction === 'W') value = -Math.abs(value);
  else if (direction === 'N' || direction === 'E') value = Math.abs(value);

  return Number.isFinite(value) ? value : null;
};

const orderAsLngLat = (a, b, strA = '', strB = '') => {
  const aIsLat = /[NS]/i.test(strA);
  const aIsLng = /[EW]/i.test(strA);
  const bIsLat = /[NS]/i.test(strB);
  const bIsLng = /[EW]/i.test(strB);

  if (aIsLat && bIsLng) return [b, a];
  if (aIsLng && bIsLat) return [a, b];
  if (aIsLat && !bIsLng) return [b, a];
  if (bIsLat && !aIsLng) return [a, b];
  if (aIsLng && !bIsLat) return [a, b];
  if (bIsLng && !aIsLat) return [b, a];

  if (Math.abs(a) <= 90 && Math.abs(b) <= 180 && Math.abs(b) > 90) return [b, a];
  if (Math.abs(b) <= 90 && Math.abs(a) <= 180 && Math.abs(a) > 90) return [a, b];
  if (Math.abs(a) <= 90 && Math.abs(b) <= 180) return [b, a];
  if (Math.abs(b) <= 90 && Math.abs(a) <= 180) return [a, b];
  return null;
};

/**
 * Parse a coordinate pair into GeoJSON order [longitude, latitude].
 * Supports decimal and DMS formats.
 * @param {string} str
 * @returns {[number, number]|null}
 */
export const parseCoordinatePair = (str) => {
  if (!str || typeof str !== 'string') return null;
  const trimmed = str.trim().replace(/,+\s*$/, '').trim();
  if (!trimmed) return null;

  if (trimmed.includes(',')) {
    const parts = trimmed.split(',').map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const valA = parseCoordinateComponent(parts[0]);
      const valB = parseCoordinateComponent(parts[1]);
      if (valA != null && valB != null) {
        const ordered = orderAsLngLat(valA, valB, parts[0], parts[1]);
        if (ordered) return ordered;
      }
    }
  }

  const decPair = trimmed.replace(/°/g, '').match(/^(-?\d{1,3}(?:\.\d+)?)[,\s]+(-?\d{1,3}(?:\.\d+)?)$/);
  if (decPair) {
    const a = parseFloat(decPair[1]);
    const b = parseFloat(decPair[2]);
    if (Number.isFinite(a) && Number.isFinite(b)) {
      return orderAsLngLat(a, b);
    }
  }

  return null;
};

const EARTH_RADIUS_KM = 6371;

/**
 * Build a GeoJSON polygon approximating a circle on the Earth's surface.
 * @param {number} lng - Center longitude
 * @param {number} lat - Center latitude
 * @param {number} radiusKm - Radius in kilometres
 * @param {number} steps - Polygon vertex count
 * @returns {{ type: string, geometry: object, properties: object }}
 */
export const hasValidCoordinates = (location) =>
  Boolean(
    location?.coordinates &&
      Array.isArray(location.coordinates) &&
      location.coordinates.length >= 2 &&
      Number.isFinite(location.coordinates[0]) &&
      Number.isFinite(location.coordinates[1])
  );

export const getHomePortDisplay = (vessel, appUser, fallback = 'N/A') =>
  vessel?.homePort?.trim() ||
  vessel?.location?.address?.trim() ||
  appUser?.location?.trim() ||
  appUser?.currentLocation?.address?.trim() ||
  fallback;

export const createCircleGeoJSON = (lng, lat, radiusKm, steps = 64) => {
  const coords = [];
  const latRad = (lat * Math.PI) / 180;
  const lngRad = (lng * Math.PI) / 180;
  const angDist = radiusKm / EARTH_RADIUS_KM;

  for (let i = 0; i <= steps; i += 1) {
    const bearing = ((i / steps) * 360 * Math.PI) / 180;
    const lat2 = Math.asin(
      Math.sin(latRad) * Math.cos(angDist) +
        Math.cos(latRad) * Math.sin(angDist) * Math.cos(bearing)
    );
    const lng2 =
      lngRad +
      Math.atan2(
        Math.sin(bearing) * Math.sin(angDist) * Math.cos(latRad),
        Math.cos(angDist) - Math.sin(latRad) * Math.sin(lat2)
      );
    coords.push([(lng2 * 180) / Math.PI, (lat2 * 180) / Math.PI]);
  }

  return {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [coords] },
    properties: {},
  };
};
